import React, { FunctionComponent, useEffect, useState, useRef } from 'react';
import { ProductTag } from './types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { fa1, fa2, fa3, faCircle, faClose, faUpload, faSpinner, fa4 } from '@fortawesome/free-solid-svg-icons'
import api from './api'
import { Link, Navigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

interface ProductName {
    brand: ProductTag;
    class: ProductTag,
    age: ProductTag,
    price: number
}
enum EnhancementChoice {
    NoChoice,
    RequestRefurbish,
    DeclineRefurbish,
    ProvenAlreadyRefurbished,
    NoProofAlreadyRefurbished
}
// TO DO, implement primary image selection
const ListForm: FunctionComponent<{ tags: Array<ProductTag>, onSubmitted: (id: string) => void }> = ({ tags, onSubmitted }) => {
    const [name, setName] = useState(undefined as ProductName | undefined);
    const [condition, setCondition] = useState(undefined as ProductTag | undefined);
    const [enhacementChoice, setEnhancementChoice] = useState(EnhancementChoice.NoChoice)
    const [stage, setStage] = useState(0);
    const [nextStageReady, setNextStageReady] = useState(false);
    const [images, setImages] = useState([] as Array<string>);
    const [primaryImage, setPrimaryImage] = useState(-1);
    const [posting, setPosting] = useState(false);
    //TODO each stage fades in, any options under it slide down under the text for the name of the stage.
    //TODO Ask for image first, show image throughout while the user fills in rest of the form
    const submit = () => {
        if (!name) return;
        const tags = [name?.brand.alias, name?.class.alias, name?.age.alias, condition?.alias] as Array<string>
        const price = name?.price

        api.postListing(tags, price, images, primaryImage)
            .then(id => {
                onSubmitted(id);
            })
            .catch(err => {
                setPosting(false);
            })
    }

    const nextStage = () => {
        if (name && condition && enhacementChoice && images.length) {
            setStage(4)
        } else if (name && condition && enhacementChoice) {
            setStage(3);
        } else if (name && condition) {
            setStage(2);
        } else if (name) {
            setStage(1);
        }
    }
    useEffect(() => {
        if (stage === 0 && name) {
            setNextStageReady(true);
        } else if (stage === 1 && condition) {
            setNextStageReady(true);
        } else if (stage === 2 && enhacementChoice) {
            setNextStageReady(true);
        } else if (stage === 3 && images.length) {
            setNextStageReady(true)
        } else {
            setNextStageReady(false);
        }
    }, [name, condition, enhacementChoice, images, stage])

    const submitReady = stage === 3 && nextStageReady;
    const nextStageClick = () => {
        if (nextStageReady && !posting) {
            if (submitReady)
                submit();
            else
                nextStage();
        }
    }
    return <div>
        <div className='row'>
            <div className='offset-md-11 col-md-1'>
                <button
                    className='btn btn-primary btn-lg'
                    disabled={!nextStageReady || posting}
                    onClick={() => nextStageClick()} >{submitReady ? <span>SUBMIT {posting ? <FontAwesomeIcon icon={faSpinner} spin={true} /> : null}</span> : <span>NEXT</span>}</button>

            </div>
        </div>
        <div className='row'>
            <div className='col'>
                <NameForm tags={tags} onComplete={name => setName(name)} onNext={nextStageClick} />
            </div>
        </div>
        <div className='row my-5'>
            <div className='col'>
                <ConditionForm tags={tags} onTag={tag => setCondition(tag)} tag={condition} show={stage > 0} />
            </div>
        </div>
        <div className='row my-5'>
            <div className='col'>
                <ConditionOptionals
                    refurbished={condition && condition.alias === 'refurbished' || false}
                    show={stage > 1}
                    choice={enhacementChoice}
                    onChoice={choice => setEnhancementChoice(choice)} />
            </div>
        </div>
        <div className='row my-5'>
            <div className='col'>
                <ItemImages
                    show={stage > 2}
                    images={images}
                    primaryImage={primaryImage}
                    onPrimaryImage={index => setPrimaryImage(index)}
                    onNextImages={images => setImages(images)} />
            </div>
        </div>
    </div>
}
const TagSelect: FunctionComponent<{
    tags: Array<ProductTag>,
    tabOffset: number,
    onTag: (tag: ProductTag | undefined) => void,
    tag?: ProductTag,
    placeholder: string
}> = ({ placeholder, tags, tabOffset, tag, onTag, }) => {
    const [show, setShow] = useState(false);
    const [searchText, setSearchText] = useState('')
    let timeoutId: NodeJS.Timeout;

    return <div className='dropdown' onBlur={(e) => {
        console.log(e.relatedTarget)
        timeoutId = setTimeout(() => setShow(false));

        //!e.currentTarget.contains(e.relatedTarget) && setShow(false)
    }}
        onFocus={(e) => {
            clearTimeout(timeoutId);
            setShow(true);
        }}>
        <input
            type='text'
            className='form-control form-control-lg dropdown-toggle'
            placeholder={placeholder}
            value={tag ? tag.title : searchText}
            tabIndex={tabOffset}
            onChange={(e) => {
                if (tag) {
                    onTag(undefined);

                }
                setSearchText(e.currentTarget.value)
            }} />
        <div className={"dropdown-menu " + (show ? 'show' : '')}>
            {tags
                .filter(tag => {
                    if (!searchText) return true;
                    return tag.alias.startsWith(searchText.toLowerCase())
                        || tag.title.toLowerCase().startsWith(searchText.toLowerCase());
                })
                .map((tag, i) => <span className='dropdown-item' onKeyUp={e => e.key === 'Enter' && e.currentTarget.click()} onClick={() => {
                    onTag(tag)
                    setShow(false)
                }} tabIndex={i + tabOffset + 1}>{tag.title}</span>)}
        </div>
    </div>
}
const NameForm: FunctionComponent<{
    tags: Array<ProductTag>,
    onComplete: (name: ProductName | undefined) => void,
    onNext: () => void
}> = ({ tags, onComplete, onNext }) => {

    const [brandTag, setBrandTag] = useState(undefined as ProductTag | undefined);
    const [classTag, setClassTag] = useState(undefined as ProductTag | undefined);
    const [ageTag, setAgeTag] = useState(undefined as ProductTag | undefined);
    const [price, setPrice] = useState(0);
    const [priceString, setPriceString] = useState('0');

    const brandTags = tags.filter(tag => tag.type === 'brand');
    const classTags = tags.filter(tag => tag.type === 'classification')
    const ageTags = tags.filter(tag => tag.type === 'age');
    console.log('Look:', tags)
    useEffect(() => {
        if (brandTag && classTag && ageTag && price) {
            onComplete({ brand: brandTag, class: classTag, age: ageTag, price })
        } else {
            onComplete(undefined);
        }
    }, [brandTag, classTag, ageTag, price])

    return <div>
        <div className='d-flex align-items-center mx-3'>
            <span className="fa-layers fa-fw fa-1x mx-2">
                <FontAwesomeIcon icon={faCircle} size='lg' />
                <FontAwesomeIcon icon={fa1} inverse size='xs' />
            </span>
            <span className='display-5'>NAME</span>
        </div>
        <div className='mx-5 my-3'>
            <div className='row'>
                <div className='col-lg-2 col-md-3'>
                    <TagSelect
                        placeholder='Brand'
                        tags={brandTags}
                        tabOffset={10}
                        onTag={tag => {
                            console.log(tag);
                            setBrandTag(tag)
                        }}
                        tag={brandTag}
                    />
                </div>
                <div className='col-lg-2 col-md-3'>
                    <TagSelect
                        placeholder='Type'
                        tags={classTags}
                        tabOffset={20}
                        onTag={tag => {
                            setClassTag(tag)
                        }}
                        tag={classTag}
                    />
                </div>
                <div className='col-lg-2 col-md-3'>
                    <TagSelect
                        placeholder='Age'
                        tags={ageTags}
                        tabOffset={30}
                        onTag={tag => {
                            setAgeTag(tag)
                        }}
                        tag={ageTag}
                    />
                </div>
                <div className='col-lg-2 col-md-3'>
                    <input className='form-control form-control-lg' value={'$ ' + priceString} onKeyDown={e => e.key === 'Enter' && onNext()} onChange={e => {
                        const input = e.target.value;
                        const priceString = input.replace(/[^0-9.]/g, "")
                        const price = parseFloat(priceString || '0');
                        setPriceString(priceString);
                        setPrice(price);
                    }} />
                </div>
            </div>
        </div>
    </div>
}

const ConditionForm: FunctionComponent<{ tags: Array<ProductTag>, tag: ProductTag | undefined, onTag: (tag: ProductTag | undefined) => void, show: boolean }> = ({ tags, onTag, tag: selectedTag, show }) => {
    const conditionTags = tags.filter(({ type }) => type === 'condition');
    console.log(conditionTags)
    return <div>
        <div className={'d-flex align-items-center mx-3 slide-right ' + (show ? 'visible' : 'invisible')}>
            <span className="fa-layers fa-fw fa-1x mx-2">
                <FontAwesomeIcon icon={faCircle} size='lg' />
                <FontAwesomeIcon icon={fa2} inverse size='xs' />
            </span>
            <span className='display-5 me-3'>CONDITION</span>
            <span>
                {conditionTags.map(tag => <button
                    className={'btn btn-outline-primary btn-lg mx-2 ' + (selectedTag?.alias === tag.alias ? 'active' : '')}
                    onClick={() => onTag(tag)}>{tag.title}</button>)}
            </span>
        </div>
    </div>
}
const ConditionOptionals: FunctionComponent<{ show: boolean, refurbished: boolean, choice: EnhancementChoice, onChoice: (choice: EnhancementChoice) => void }> = ({ show, refurbished, choice, onChoice }) => {
    return <div>
        <div className={classNames('d-flex align-items-center mx-3 slide-right ', show ? 'visible' : 'invisible')}>
            <span className="fa-layers fa-fw fa-1x mx-2">
                <FontAwesomeIcon icon={faCircle} size='lg' />
                <FontAwesomeIcon icon={fa3} inverse size='xs' />
            </span>
            <span className='display-5 me-3'>REFURBISHMENT</span>
        </div>
        <div className={classNames('mx-3 p-3 slide-right-slower ', show ? 'visible' : 'invisible')}>
            {!refurbished && <div>
                <span className='display-6 mx-5'>Would you like to have this item refurbished?</span>
                <div className='mx-5 my-3'>
                    <button
                        onClick={() => onChoice(EnhancementChoice.RequestRefurbish)}
                        className={'btn btn-outline-primary btn-lg mx-2 ' + (choice === EnhancementChoice.RequestRefurbish ? 'active' : '')}>Yes</button>
                    <button
                        onClick={() => onChoice(EnhancementChoice.DeclineRefurbish)}
                        className={'btn btn-outline-secondary btn-lg mx-2 ' + (choice === EnhancementChoice.DeclineRefurbish ? 'active' : '')}>No</button>
                </div>
            </div>}
            {refurbished && <div>
                <div><span className='display-6 mx-5'>Last refurbishment documentation</span></div>
                <div className='mx-5 my-3'>
                    <button
                        onClick={() => onChoice(EnhancementChoice.ProvenAlreadyRefurbished)}
                        className={'btn btn-outline-primary btn-lg mx-2 ' + (choice === EnhancementChoice.ProvenAlreadyRefurbished ? 'active' : '')}>Select Files (PDF or image)</button>
                    <button
                        onClick={() => onChoice(EnhancementChoice.NoProofAlreadyRefurbished)}
                        className={'btn btn-outline-secondary btn-lg mx-2 ' + (choice === EnhancementChoice.NoProofAlreadyRefurbished ? 'active' : '')}>Skip</button>
                </div>
            </div>}
        </div>
    </div>
}
const ItemImages: FunctionComponent<{
    show: boolean,
    images: Array<string>,
    primaryImage: number,
    onPrimaryImage: (index: number) => void,
    onNextImages: (images: Array<string>) => void
}> = ({ show, images, primaryImage, onPrimaryImage, onNextImages }) => {
    const uploadInput = useRef(null as any as HTMLInputElement);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [append, setAppend] = useState([] as Array<string>);

    useEffect(() => {
        if (uploadInput && uploadInput.current) {
            const currentInput = uploadInput.current;

            const listener = () => {
                api.uploadFiles(currentInput.files, percent => {
                    setProgress(percent)
                }, files => {
                    setAppend(files)
                }, () => {
                    setUploading(false);
                })
            }
            currentInput.addEventListener('change', listener)
            return () => currentInput.removeEventListener('change', listener);
        }
    }, [uploadInput]);

    useEffect(() => {
        if (!append.length) return;
        const toAppend = append;
        setAppend([]);
        onNextImages([...images, ...toAppend]);
    }, [append])


    return <div>
        <div className={'d-flex align-items-center mx-3 slide-right ' + (show ? 'visible' : 'invisible')}>
            <span className="fa-layers fa-fw fa-1x mx-2">
                <FontAwesomeIcon icon={faCircle} size='lg' />
                <FontAwesomeIcon icon={fa4} inverse size='xs' />
            </span>
            <span className='display-5 me-3'>IMAGES</span>
        </div>
        <div className={'mx-3 p-3 slide-right ' + (show ? 'visible' : 'invisible')}>
            <div className='row'>
                <div className='col'>
                    <div className='row'>
                        {images.map(((image, i) => <div className='col-md-2'>
                            <div
                                className={'card shadow-sm justify-content-center ' + (primaryImage === i ? 'border-secondary' : '')}
                                style={{ 'borderWidth': primaryImage === i ? '8px' : '1px' }}
                                onClick={() => onPrimaryImage(i)}>
                                <div className='remove-button'>
                                    <button type="button" className="btn btn-link text-secondary" onClick={() => onNextImages(images.filter(_image => _image !== image))}>
                                        <FontAwesomeIcon icon={faClose} />
                                    </button>
                                </div>
                                <img src={'/api/image?id=' + image} className='product-image product-image-real' style={{ 'objectFit': 'scale-down' }} />
                            </div>
                        </div>))}
                        <input type='file' ref={uploadInput} className='d-none' multiple />
                        <div className='col-md-2'>
                            <div className='card shadow-sm product-image justify-content-center cursor-pointer' onClick={() => !uploading && uploadInput.current.click()}>
                                <div className='add-photo product-image d-flex align-items-center justify-content-center' >
                                    <div className="">
                                        <h2>
                                            {!uploading
                                                ? <>
                                                    <FontAwesomeIcon icon={faUpload}></FontAwesomeIcon>
                                                    <span className='ms-2'>
                                                        Select File
                                                    </span>
                                                </>
                                                : <>
                                                    <FontAwesomeIcon icon={faSpinner} spin={true} />
                                                    <span className='ms-2'>
                                                        {progress}%
                                                    </span>
                                                </>}
                                            <strong>

                                            </strong>
                                        </h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}
export const ListFormRoute: FunctionComponent<{ tags: Array<ProductTag> }> = ({ tags }) => {
    const [listingId, setListingId] = useState('');
    return listingId ? <Navigate to={'/list/success/' + listingId} /> : <ListForm tags={tags} onSubmitted={id => setListingId(id)} />
}

const ListSuccess: FunctionComponent<{ id: string }> = ({ id }) => {
    const link = id ? <span> at <Link to={'/listing/' + id}>{'/listing/' + id}</Link></span> : undefined
    return <div>
        <h2>Success!</h2>
        <span>Your listing has been posted and should be available soon{link}.</span>
    </div>
}

export const ListSuccessRoute: FunctionComponent<{}> = ({ }) => {
    const { id } = useParams();
    const realId = id || ''
    return <ListSuccess id={realId} />
}